import cx from 'classnames';
import isAfter from 'date-fns/isAfter';
import parseISO from 'date-fns/parseISO';

import { makeStyles } from '@mui/styles';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ClearIcon from '@mui/icons-material/Clear';
import DoneIcon from '@mui/icons-material/Done';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AssignmentIcon from '@mui/icons-material/Assignment';

import LearningOutcome from '../../../icons/LearningOutcome';
import CircleArrowIcon from '../../../icons/CircleArrow';
import OCAssessmentIcon from '../../../icons/Assessment';
import PlannedIcon from '../../../icons/PlannedIcon';
import ToBeCompletedIcon from '../../../icons/ToBeCompletedIcon';
import CompletedIcon from '../../../icons/CompletedIcon';
import CanceledIcon from '../../../icons/CanceledIcon';
import StudentAbsentIcon from '../../../icons/StudentAbsentIcon';

import statuses from '../../constants/statuses';

import useColorByStatus from '../../hooks/useColorByStatus';

const useStyles = makeStyles(
  ({ spacing, typography }) => ({
    root: {
      ...typography.caption,
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      textAlign: 'center',
      flexShrink: 0,
      boxSizing: 'border-box !important',
      border: '1px solid',
      color: 'var(--color)',
      borderColor: 'var(--color)',
      width: spacing(3),
      height: spacing(3),
      '& $icon': {
        width: 'var(--iconSizeSmall)',
        height: 'var(--iconSizeSmall)',
      },
    },
    big: {
      width: spacing(5),
      height: spacing(5),
      '& $icon': {
        width: 'var(--iconSizeBig)',
        height: 'var(--iconSizeBig)',
      },
    },
    icon: {},
    number: {},
  }),
  { classNamePrefix: 'LearningActivityStatus' }
);

// return correct size applied to svg icon
// some of them should be displayed smaller or larger in order
// to make them visually the same size
const getIconSize = (icon) => {
  const SMALLER = { small: '0.5em', big: '0.8em' };
  const NORMAL = { small: '0.6em', big: '1em' };
  const LARGER = { small: '0.75em', big: '1.2em' };

  const iconSizes = new Map();
  iconSizes.set(ClearIcon, LARGER);
  iconSizes.set(DoneIcon, LARGER);
  iconSizes.set(RotateRightIcon, LARGER);
  iconSizes.set(CircleArrowIcon, SMALLER);
  iconSizes.set(OCAssessmentIcon, LARGER);

  return iconSizes.get(icon) || NORMAL;
};

// return correct icon component
const getIcon = (activityType, status, sessionDate, currentDate, isAfterNow) => {
  if (status === statuses.STATUS_NOT_STARTED) {
    const activityTypeToIcon = {
      skill: LearningOutcome,
      learningOutcome: LearningOutcome,
      project: AssignmentIcon,
      quiz: AssignmentTurnedInIcon,
    };

    return activityTypeToIcon[activityType] || null;
  }

  const isPlanned = statuses.STATUS_PENDING && isAfterNow;
  const pendingIcon = !isPlanned ? ToBeCompletedIcon : PlannedIcon;

  const icons = {
    [statuses.STATUS_IN_PROGRESS]: RotateRightIcon,
    [statuses.STATUS_AWAITING_REVIEW]: ScheduleIcon,
    [statuses.STATUS_PASSED]: DoneIcon,
    [statuses.STATUS_CAN_RETRY]: CircleArrowIcon,
    [statuses.STATUS_FAILED]: ClearIcon,
    [statuses.STATUS_READY_FOR_REVIEW]: OCAssessmentIcon,

    [statuses.STATUS_CANCELED]: CanceledIcon,
    [statuses.STATUS_LATE_CANCELED]: CanceledIcon,
    [statuses.STATUS_STUDENT_ABSENT]: StudentAbsentIcon,
    [statuses.STATUS_PENDING]: pendingIcon,
    [statuses.STATUS_COMPLETED]: CompletedIcon,
  };

  return icons[status];
};

export default function LearningActivityStatus({
  activityType = '',
  className = '',
  status = statuses.STATUS_NOT_STARTED,
  number = null,
  isBig = false,
  isIconHidden = false,
  forcedIcon: ForcedIcon = null,
  currentDate = null,
  sessionDate = null,
  ariaLabel = null,
}) {
  const classes = useStyles();
  const isAfterNow = isAfter(parseISO(sessionDate), parseISO(currentDate));
  const isPlanned = statuses.STATUS_PENDING && isAfterNow;

  const pendingColor = !isPlanned
    ? useColorByStatus(statuses.STATUS_AWAITING_REVIEW)
    : useColorByStatus(statuses.STATUS_PENDING);
  const iconColor = status !== statuses.STATUS_PENDING ? useColorByStatus(status) : pendingColor;

  const Icon = ForcedIcon || getIcon(activityType, status, sessionDate, currentDate, isAfterNow);
  const iconSizes = getIconSize(Icon);

  const content =
    Icon && !isIconHidden ? (
      <Icon aria-label={ariaLabel} className={classes.icon} />
    ) : (
      <span className={classes.number}>{number}</span>
    );

  return (
    <div
      style={{
        '--color': iconColor,
        '--iconSizeSmall': iconSizes.small,
        '--iconSizeBig': iconSizes.big,
      }}
      className={cx(classes.root, className, { [classes.big]: isBig })}
    >
      {content}
    </div>
  );
}
